import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../api/endpoints';
import { Table, type Column, Badge, TableSkeleton, CoreVenueModal, CoreVenueFieldEditor } from '../components/Common';
import type { AdminCoreVenue, AdminCoreVenueUpdate, Address, MonthPeriod, Month } from '../types';
import { externalUrl } from '../utils/externalUrl';
import clsx from 'clsx';
import {
  Music, Beer, Guitar, UtensilsCrossed, Hotel, Building2, Tent, Users, Landmark, HelpCircle,
  ExternalLink, Check, X, MousePointerClick, PartyPopper, Trophy, GraduationCap, Briefcase,
} from 'lucide-react';

const SaxophoneIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2l-4 4 8 8 4-4z" />
    <path d="M10 6L4 12c-2 2-2 5 0 7s5 2 7 0l6-6" />
    <circle cx="8" cy="16" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="18" r="1" />
  </svg>
);

const VENUE_TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  concert_hall: Music,
  bar: Beer,
  concert_bar: Guitar,
  jazz_club: SaxophoneIcon,
  restaurant: UtensilsCrossed,
  hotel: Hotel,
  city_concert_hall: Building2,
  festival: Tent,
  association: Users,
  city: Landmark,
  comite_des_fetes: PartyPopper,
  competition: Trophy,
  mentoring: GraduationCap,
  booking_agency: Briefcase,
  other: HelpCircle,
};

const MONTH_LABELS: Record<string, string> = {
  january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr',
  may: 'May', june: 'Jun', july: 'Jul', august: 'Aug',
  september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
};

const MONTH_ORDER: Month[] = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const formatMonthPeriod = (period: MonthPeriod | undefined): string => {
  if (!period) return '-';
  if (period.all_year) return 'All Year';
  if (period.months.length === 0) return '-';

  const indices = period.months
    .map(m => MONTH_ORDER.indexOf(m))
    .filter(i => i !== -1)
    .sort((a, b) => a - b);

  if (indices.length === 1) {
    return MONTH_LABELS[MONTH_ORDER[indices[0]]];
  }

  // Check for consecutive range
  const isConsecutive = indices.every((val, i) => i === 0 || val === indices[i - 1] + 1);
  if (isConsecutive) {
    return `${MONTH_LABELS[MONTH_ORDER[indices[0]]]} to ${MONTH_LABELS[MONTH_ORDER[indices[indices.length - 1]]]}`;
  }

  return indices.map(i => MONTH_LABELS[MONTH_ORDER[i]]).join(', ');
};

function getColumns(onQuickAction: (venue: AdminCoreVenue, action: 'accept' | 'refuse') => void): Column<AdminCoreVenue>[] {
  return [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '110px',
      render: (row) => {
        const Icon = VENUE_TYPE_ICONS[row.type || ''] || HelpCircle;
        return (
          <span className="inline-flex items-center gap-1.5 text-xs text-dark-text-secondary">
            <Icon size={14} className="shrink-0" />
            {row.type?.replace('_', ' ') ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'styles',
      label: 'Style',
      width: '110px',
      render: (row) => {
        if (!row.styles || row.styles.length === 0) {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{row.styles.join(', ')}</span>;
      },
    },
    {
      key: 'name',
      label: 'Venue Name',
      sortable: true,
      width: '200px',
      render: (row) => {
        const inactive = row.status === 'closed' || row.status === 'no_concerts';
        return (
          <div>
            <div className={clsx('font-medium', inactive && 'line-through text-dark-text-muted')}>
              {row.name}
            </div>
            {inactive && (
              <div className="text-xs text-dark-text-muted">
                {row.status === 'closed' ? 'Closed' : 'No Concerts'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'address',
      label: 'Address',
      width: '160px',
      render: (row) => {
        const address = row.address as Address | undefined;
        if (!address) return <span className="text-dark-text-muted">-</span>;

        const hasStructured = address.city || address.administrative_region_1 || address.administrative_region_2 || address.country;

        if (!hasStructured && address.unparsed_address) {
          return <div className="text-xs text-dark-text-secondary leading-tight">{address.unparsed_address}</div>;
        }

        if (!hasStructured) {
          return <span className="text-dark-text-muted">-</span>;
        }

        return (
          <div className="flex flex-col text-xs leading-tight">
            {address.city && <span className="text-dark-text-primary">{address.city}</span>}
            {address.administrative_region_1 && <span className="text-dark-text-secondary">{address.administrative_region_1}</span>}
            {address.administrative_region_2 && <span className="text-dark-text-muted">{address.administrative_region_2}</span>}
            {address.country && <span className="text-dark-text-muted">{address.country}</span>}
          </div>
        );
      },
    },
    {
      key: 'concert_period',
      label: 'Concert Period',
      width: '100px',
      render: (row) => {
        const text = formatMonthPeriod(row.concert_period);
        if (text === '-') {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{text}</span>;
      },
    },
    {
      key: 'url',
      label: 'URL',
      width: '60px',
      render: (row) => {
        const href = externalUrl(row.url);
        if (!href) return <span className="text-dark-text-muted">-</span>;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-primary-400 hover:text-primary-300 transition-colors"
            title={row.url}
          >
            <ExternalLink size={14} />
          </a>
        );
      },
    },
    {
      key: 'owner_email',
      label: 'Owner',
      sortable: true,
      width: '180px',
      render: (row) => (
        <span className="text-xs text-dark-text-secondary">{row.owner_email || row.owner_user_id}</span>
      ),
    },
    {
      key: 'actions' as keyof AdminCoreVenue,
      label: '',
      width: '80px',
      render: (row) => {
        const canAccept = row.visibility === 'pending_public' || row.visibility === 'refused_public';
        const canRefuse = row.visibility === 'public' || row.visibility === 'pending_public';
        return (
          <div className="flex items-center gap-1">
            {canAccept && (
              <button
                onClick={(e) => { e.stopPropagation(); onQuickAction(row, 'accept'); }}
                className="p-1 rounded-sm hover:bg-emerald-900/30 text-dark-text-muted hover:text-emerald-400 transition-colors"
                title="Accept as public"
              >
                <Check size={14} />
              </button>
            )}
            {canRefuse && (
              <button
                onClick={(e) => { e.stopPropagation(); onQuickAction(row, 'refuse'); }}
                className="p-1 rounded-sm hover:bg-red-900/30 text-dark-text-muted hover:text-red-400 transition-colors"
                title="Refuse public status"
              >
                <X size={14} />
              </button>
            )}
          </div>
        );
      },
    },
  ];
}

function VenueTable({ title, venues, variant, onRowClick, onQuickAction, onCellRightClick }: {
  title: string;
  venues: AdminCoreVenue[];
  variant: 'warning' | 'success' | 'error';
  onRowClick: (venue: AdminCoreVenue) => void;
  onQuickAction: (venue: AdminCoreVenue, action: 'accept' | 'refuse') => void;
  onCellRightClick?: (row: AdminCoreVenue, columnKey: string, position: { x: number; y: number }) => void;
}) {
  const columns = getColumns(onQuickAction);
  return (
    <div className="card animate-scaleIn flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-dark-border flex items-center gap-3">
        <h2 className="text-lg font-semibold text-dark-text-primary">
          {title}
        </h2>
        <Badge variant={variant}>{venues.length}</Badge>
      </div>
      {venues.length === 0 ? (
        <div className="px-4 py-8 text-center text-dark-text-muted">
          No venues
        </div>
      ) : (
        <Table
          data={venues}
          columns={columns}
          searchable={true}
          searchPlaceholder="Search venues..."
          emptyMessage="No venues match your search"
          onRowClick={onRowClick}
          onCellRightClick={onCellRightClick}
        />
      )}
    </div>
  );
}

export function CoreVenuesPage() {
  const queryClient = useQueryClient();
  const [editingVenue, setEditingVenue] = useState<AdminCoreVenue | null>(null);
  const [isQuickEditEnabled, setIsQuickEditEnabled] = useState(() => localStorage.getItem('core-venues-quick-edit') === 'true');
  const [quickEditState, setQuickEditState] = useState<{ venue: AdminCoreVenue; field: string; position: { x: number; y: number } } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'core-venues'],
    queryFn: () => adminAPI.getCoreVenues().then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ venueId, data }: { venueId: string; data: AdminCoreVenueUpdate }) =>
      adminAPI.updateCoreVenue(venueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'core-venues'] });
    },
  });

  const handleSubmit = async (venueId: string, updateData: AdminCoreVenueUpdate) => {
    await updateMutation.mutateAsync({ venueId, data: updateData });
  };

  const handleQuickAction = async (venue: AdminCoreVenue, action: 'accept' | 'refuse') => {
    const newVisibility = action === 'accept' ? 'public' : 'refused_public';
    await updateMutation.mutateAsync({
      venueId: venue.id,
      data: { visibility: newVisibility },
    });
  };

  const handleToggleQuickEdit = () => {
    const next = !isQuickEditEnabled;
    setIsQuickEditEnabled(next);
    localStorage.setItem('core-venues-quick-edit', String(next));
  };

  const handleCellRightClick = (row: AdminCoreVenue, columnKey: string, position: { x: number; y: number }) => {
    setQuickEditState({ venue: row, field: columnKey, position });
  };

  const handleFieldSave = async (venueId: string, updateData: AdminCoreVenueUpdate) => {
    await updateMutation.mutateAsync({ venueId, data: updateData });
  };

  return (
    <div className="h-full flex flex-col animate-fadeIn">
    <div className="max-w-(--breakpoint-xl) mx-auto w-full p-6 flex-1 flex flex-col min-h-0 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-text-primary tracking-tight">Core Venues</h1>
        <button
          onClick={handleToggleQuickEdit}
          title={isQuickEditEnabled ? 'Disable quick edit (right-click)' : 'Enable quick edit (right-click)'}
          className={clsx(
            'p-2 rounded-lg border transition-colors',
            isQuickEditEnabled
              ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
              : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
          )}
        >
          <MousePointerClick className="w-4 h-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="card">
          <TableSkeleton rows={5} />
        </div>
      ) : (
        <>
          <VenueTable
            title="Pending Review"
            venues={data?.pending ?? []}
            variant="warning"
            onRowClick={setEditingVenue}
            onQuickAction={handleQuickAction}
            onCellRightClick={isQuickEditEnabled ? handleCellRightClick : undefined}
          />
          <VenueTable
            title="Public"
            venues={data?.public ?? []}
            variant="success"
            onRowClick={setEditingVenue}
            onQuickAction={handleQuickAction}
            onCellRightClick={isQuickEditEnabled ? handleCellRightClick : undefined}
          />
          <VenueTable
            title="Refused"
            venues={data?.refused ?? []}
            variant="error"
            onRowClick={setEditingVenue}
            onQuickAction={handleQuickAction}
            onCellRightClick={isQuickEditEnabled ? handleCellRightClick : undefined}
          />
        </>
      )}
    </div>

    <CoreVenueModal
      isOpen={editingVenue !== null}
      onClose={() => setEditingVenue(null)}
      onSubmit={handleSubmit}
      venue={editingVenue}
    />

    {quickEditState && (
      <CoreVenueFieldEditor
        venue={quickEditState.venue}
        field={quickEditState.field}
        position={quickEditState.position}
        onSave={handleFieldSave}
        onClose={() => setQuickEditState(null)}
      />
    )}
    </div>
  );
}
